import React from 'react';
import { Settings } from '@/Settings';
import { BlockPreviewComponent } from '../../lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { TextBlock } from '../../../declarations/models/blocks/TextBlock';
import { Page } from '../../../declarations/models/Page';
import { useEditorModel } from '../../lib/components/EditorDataProvider';
import { ImagePreview } from '../../../components/ImagePreview';
import { BlockModuleType } from '../../../declarations/models/BlockModuleType';
import LinksPreview from './LinksPreview';
import { isDamsMediaItem, isM24MediaModel } from '../../../utils/typeChecks';
import { calculateObjectPosition } from '../../../components/Image';

export const TextBlockPreview: BlockPreviewComponent = () => {
  const { block } = useCurrentBlock();
  const currentBlock = block as TextBlock;
  const model: Page = useEditorModel<Page>();
  const children = model.children as Array<Page>;

  const linkItems = currentBlock?.listchildren
    ? (currentBlock?.items as Array<Page>).concat(children)
    : currentBlock?.items;

  const media = currentBlock?.mediaobject;
  const caption = media?.local?.caption || (isM24MediaModel(media) && media.content?.caption);

  const damsEmsDomain = Settings.viteEnvironmentIsProduction ? 'ems.dimu.org' : 'ems-test.dimu.org';
  let imgUrl = '';
  if (currentBlock.mediaobject && isDamsMediaItem(currentBlock?.mediaobject)) {
    imgUrl = currentBlock?.mediaobject.m24?.ems_id
      ? `https://${damsEmsDomain}/image/${currentBlock?.mediaobject.m24.ems_id}`
      : '';
  } else if (currentBlock?.mediaobject && 'url' in currentBlock.mediaobject && currentBlock.mediaobject.url) {
    imgUrl = currentBlock.mediaobject.url.replace('mm.', 'ems.');
  }

  return (
    <div className='article__grid' data-layout={currentBlock?.layout || 'auto'}>
      {currentBlock?.module__type !== BlockModuleType.MODULE && (
        <div
          className={`article__content layout--${currentBlock?.layout || 'auto'}`}
          data-title-visible={!!(currentBlock?.visibletitle && currentBlock?.title)}>
          {currentBlock?.mediaobject && (
            <figure
              data-fullscreen='false'
              className={`media article__media module--media media-format--auto layout--${
                currentBlock?.layout || 'auto'
              }`}>
              <ImagePreview mediaObject={currentBlock?.mediaobject}>
                {!currentBlock?.mediaobject?.local?.disableCaption && (
                  <div className='module__content'>
                    <div className='module__body'>
                      <figcaption className='media__caption'>
                        {caption || ''}

                        <span className='media__credit  caption--credit'>
                          {currentBlock?.mediaobject?.content?.credits &&
                            currentBlock?.mediaobject?.content?.credits?.length > 0 &&
                            currentBlock?.mediaobject?.content?.credits?.map(
                              (credit) =>
                                credit?.name && (
                                  <span className={credit?.type} key={credit?.name}>
                                    {credit?.name}
                                  </span>
                                ),
                            )}
                        </span>
                        {currentBlock?.mediaobject &&
                          'source' in currentBlock.mediaobject &&
                          currentBlock.mediaobject.source === 'digitaltMuseum' && (
                            <span className='media__credit caption--dm'>digitaltmuseum.org</span>
                          )}
                      </figcaption>
                    </div>
                  </div>
                )}
                {currentBlock?.mediaobject?.url && !currentBlock?.mediaobject?.local?.disableZoom && (
                  <div className='module__action'>
                    <ul className='nav-fullscreen'>
                      <li className='nav-fullscreen-open'>
                        <button aria-label='Åpne i fullskjerm' type='button' className='button  button--fullscreen'>
                          <i className='button__icon  i-nav-fullscreen' />
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </ImagePreview>
            </figure>
          )}
          <div className='article__bodytext'>
            {currentBlock?.visibletitle && <h2>{currentBlock?.title}</h2>}
            <div className='module__body'>
              <div
                dangerouslySetInnerHTML={{ __html: currentBlock?.bodytext || '' }} // eslint-disable-line react/no-danger
              />

              {currentBlock?.itemsTitle && <strong className='article__links_title'>{currentBlock?.itemsTitle}</strong>}
              <LinksPreview linkItems={linkItems} linkStyle={currentBlock?.linkstyle} />
            </div>
          </div>
        </div>
      )}
      {currentBlock?.module__type === BlockModuleType.MODULE && (
        <>
          {currentBlock?.state === 'semi' ? (
            <div className='preview__blockmessage' style={{ marginBottom: '-1.5rem' }}>
              Tekstboks vil være delvis åpen i portal
            </div>
          ) : null}
          {currentBlock?.state === 'collapsed' ? (
            <div className='preview__blockmessage' style={{ marginBottom: '-1.5rem' }}>
              Tekstboks vil være lukket i portal
            </div>
          ) : null}
          <div className={`module module--content layout--${currentBlock?.layout || 'auto'} `}>
            <div className={`module__grid skin-contentbox ${currentBlock?.skin || 'skin'}`}>
              {currentBlock?.mediaobject && (
                <div className='module__media'>
                  <img
                    alt=''
                    style={{
                      objectPosition: calculateObjectPosition(
                        currentBlock?.mediaobject.local?.x,
                        currentBlock?.mediaobject.local?.y,
                      ),
                    }}
                    src={`${imgUrl || ''}`}
                  />

                  {!currentBlock.mediaobject?.local?.disableCaption && currentBlock.mediaobject?.content?.credits && (
                    <span className='media__credits'>
                      <span className='media__credit caption--credit'>
                        {currentBlock?.mediaobject?.content?.credits &&
                          currentBlock?.mediaobject?.content?.credits?.length > 0 &&
                          currentBlock?.mediaobject?.content?.credits?.map(
                            (credit) =>
                              credit?.name && (
                                <span className={credit?.type} key={credit?.name}>
                                  {credit?.name}
                                </span>
                              ),
                          )}
                      </span>
                    </span>
                  )}
                  {!currentBlock.mediaobject?.local?.disableCaption &&
                    currentBlock?.mediaobject &&
                    'source' in currentBlock.mediaobject &&
                    currentBlock.mediaobject.source === 'digitaltMuseum' && (
                      <span className='media__credits'>
                        <span className='media__credit caption--dm'>
                          <p>{caption || ''}</p>
                          Bildet er hentet fra digitaltmuseum.org
                        </span>
                      </span>
                    )}
                </div>
              )}
              <div className='module__content'>
                <div className='module__head'>
                  {currentBlock?.icon && <i className={`module__icon i-icon-${currentBlock?.icon}`} />}
                  {currentBlock?.visibletitle && <h2>{currentBlock?.title}</h2>}
                </div>
                <div className='module__body'>
                  {/* eslint-disable-next-line react/no-danger */}
                  <p className='modulebodytext' dangerouslySetInnerHTML={{ __html: currentBlock?.bodytext || '' }} />
                  {currentBlock?.itemsTitle && <strong>{currentBlock?.itemsTitle}</strong>}
                  <LinksPreview linkItems={linkItems} linkStyle={currentBlock?.linkstyle} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TextBlockPreview;
